import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Gauge, Loader2 } from "lucide-react";
import { MONTHS } from "@/components/PlannedLessons";

export const PacePanel = ({ scopeParams, schoolKey }) => {
  const [term, setTerm] = useState("all");
  const [data, setData] = useState(null);
  const navigate = useNavigate();
  useEffect(() => {
    setData(null);
    api.get("/diary/pace-panel", { params: { ...scopeParams, ...(term !== "all" ? { term_order: Number(term) } : {}) } })
      .then((r) => setData(r.data)).catch(() => setData({ rows: [], low: 0, missing: 0 }));
    // eslint-disable-next-line
  }, [schoolKey, term]);
  const open = (r) => navigate(`/diario?class_id=${r.class_id}&subject_id=${r.subject_id}&cn=${encodeURIComponent(r.class_name)}&sn=${encodeURIComponent(r.subject)}`);

  return (
    <Card className="mb-6 border-slate-200 p-4" data-testid="pace-panel">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="flex items-center gap-2 font-display text-base font-bold text-slate-900 md:text-lg"><Gauge className="h-5 w-5 text-red-600" /> Ritmo das aulas</h2>
          <p className="text-xs text-slate-500">Meses encerrados com aulas dadas abaixo de 80% das previstas ou sem aulas dadas informadas.</p>
        </div>
        <Select value={term} onValueChange={setTerm}>
          <SelectTrigger className="w-40" data-testid="pace-term-select"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os trimestres</SelectItem>
            {[1, 2, 3].map((t) => <SelectItem key={t} value={String(t)}>{t}º Trimestre</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      {data === null ? <Loader2 className="mx-auto h-5 w-5 animate-spin text-blue-700" /> : !data.rows.length ? (
        <p className="py-4 text-center text-sm text-slate-400" data-testid="pace-empty">Nenhum diário com ritmo abaixo do previsto.</p>
      ) : (
        <>
          <div className="mb-3 flex flex-wrap gap-2 text-xs font-semibold">
            <span className="rounded-full bg-red-100 px-2.5 py-1 text-red-700" data-testid="pace-count-low">{data.low} abaixo do previsto</span>
            <span className="rounded-full bg-amber-100 px-2.5 py-1 text-amber-700" data-testid="pace-count-missing">{data.missing} sem aulas dadas</span>
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Professor</TableHead><TableHead>Turma</TableHead><TableHead>Disciplina</TableHead><TableHead className="text-center">Tri.</TableHead>
                <TableHead>Meses com alerta</TableHead><TableHead className="text-center">Dadas / Previstas</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.rows.map((r, i) => (
                  <TableRow key={`${r.class_id}-${r.subject_id}-${r.term_order}`} className="cursor-pointer" onClick={() => open(r)} data-testid={`pace-row-${i}`}>
                    <TableCell className="font-medium text-slate-800">{r.teacher}</TableCell>
                    <TableCell>{r.class_name}</TableCell>
                    <TableCell className="text-slate-600">{r.subject}</TableCell>
                    <TableCell className="text-center">{r.term_order}º</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {r.low.map((m) => <span key={m.month} className="inline-flex items-center gap-0.5 rounded bg-red-50 px-1.5 py-0.5 text-xs font-semibold text-red-700"><AlertTriangle className="h-3 w-3" />{MONTHS[m.month]} {m.given}/{m.planned}</span>)}
                        {r.missing.map((m) => <span key={m} className="rounded bg-amber-50 px-1.5 py-0.5 text-xs font-semibold text-amber-700">{MONTHS[m]}: não informado</span>)}
                      </div>
                    </TableCell>
                    <TableCell className={`text-center font-semibold ${r.low.length ? "text-red-700" : "text-slate-600"}`}>{r.given}/{r.planned}{r.pct !== null ? ` (${r.pct}%)` : ""}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </>
      )}
    </Card>
  );
};
